import { EmailForm } from "./EmailForm";
import { LoginForm } from "./LoginForm";
import { SmsForm } from "./SmsForm";
import { TotpForm } from "./TotpForm";

/**
 * NavigationService class navigates forms into view and controls the way it is done.
 * Navigation from one form to the other is usually done using a css animation.
 */
export class NavigationService {
    private _onNavigated: (changedToFormName: string) => any;

    private _loginForm: HTMLElement;
    private _smsForm: HTMLElement;
    private _emailForm: HTMLElement;
    private _totpForm: HTMLElement;
    private _formsContainer: HTMLElement;

    constructor(onNavigated: (changedToFormName: string) => any) {
        this._onNavigated = onNavigated;
        this._loadForms();
    }

    private _loadForms(): void {
        this._loginForm = document.getElementById("login-dialog");
        this._smsForm = document.getElementById("verification-sms-dialog");
        this._emailForm = document.getElementById("verification-email-dialog");
        this._totpForm = document.getElementById("verification-totp-dialog");
        this._formsContainer = document.getElementById("dialogs-container");
    }

    public navigate(fromClassName: string, toClassName: string): void {
        this._navigateToForm(fromClassName, toClassName);
        this._onNavigated(toClassName);
    }

    private _navigateToForm(fromClassName: string, toClassName: string): void {
        this._resetStylesAndAnimations();

        const fromForm = this._classNameToFormElement(fromClassName);
        const toForm = this._classNameToFormElement(toClassName);

        // Naviguje se z verifikačního formuláře na login formulář
        if (toClassName === LoginForm.name) {
            this._slideUpToLoginForm(fromForm);
            return;
        }
        // Naviguje se z login formuláře na verifikační formulář
        if (fromClassName === LoginForm.name) {
            this._slideDownToVerificationForm(toForm);
            return;
        }
        // Mění se viditelnost mezi verifikačními formuláři, například z Email na Totp
        this._fadeInFadeOut(fromForm, toForm);
    }

    private _resetStylesAndAnimations(): void {
        this._clearVisibility();
        this._clearFadeInFadeOut();
        this._clearAnimateInvisible();
    }

    private _clearVisibility(): void {
        this._loginForm.style.visibility = "";
        this._smsForm.style.visibility = "";
        this._emailForm.style.visibility = "";
        this._totpForm.style.visibility = "";
    }

    private _clearFadeInFadeOut(): void {
        this._clearFadeAnimationFromElement(this._smsForm);
        this._clearFadeAnimationFromElement(this._emailForm);
        this._clearFadeAnimationFromElement(this._totpForm);
    }

    private _clearFadeAnimationFromElement(element: HTMLElement): void {
        element.classList.remove("fade-out-forwards");
        element.classList.remove("fade-in-forwards");
    }

    private _clearAnimateInvisible(): void {
        this._loginForm.classList.remove("animate-invisible");
        this._smsForm.classList.remove("animate-invisible");
        this._emailForm.classList.remove("animate-invisible");
        this._totpForm.classList.remove("animate-invisible");
    }

    private _classNameToFormElement(formName: string): HTMLElement {
        if (formName === SmsForm.name) {
            return this._smsForm;
        } else if (formName === EmailForm.name) {
            return this._emailForm;
        } else if (formName === TotpForm.name) {
            return this._totpForm;
        } else {
            return this._loginForm;
        }
    }

    private _slideUpToLoginForm(fromForm: HTMLElement): void {
        this._slide(fromForm, this._loginForm, "slide-up");
    }

    private _slideDownToVerificationForm(toForm: HTMLElement): void {
        this._slide(this._loginForm, toForm, "slide-down");
    }

    private _slide(fromForm: HTMLElement, toForm: HTMLElement, slideClass: string): void {
        this._clearSlide();

        this._hideVerificationForms();
        this._resetFormVisibility(fromForm);
        this._resetFormVisibility(toForm);

        // Trvání animace animate-invisible je stejné jako trvání animace slide.
        // Animace animate-invisible skryje form po uplynutí času animace
        // (je potřeba tento form označit jako invisible, i přesto že nejde vidět, a to kvůli
        // posouvání se mezi prvky formuláře pomocí tabulátoru, který by posunul tento form
        // do viditelné části).
        this._formsContainer.classList.add(slideClass); // animate slide
        fromForm.classList.add("animate-invisible");
    }

    private _clearSlide(): void {
        this._formsContainer.classList.remove("slide-up");
        this._formsContainer.classList.remove("slide-down");
    }

    private _fadeInFadeOut(fromForm: HTMLElement, toForm: HTMLElement): void {
        const fadeOutElement = fromForm;
        const fadeInElement = toForm;

        this._hideVerificationForms();
        this._resetFormVisibility(fromForm);
        this._resetFormVisibility(toForm);

        fadeOutElement.classList.add("fade-out-forwards");
        fadeInElement.classList.add("fade-in-forwards");
    }

    private _hideVerificationForms(): void {
        this._smsForm.style.visibility = "hidden";
        this._emailForm.style.visibility = "hidden";
        this._totpForm.style.visibility = "hidden";
    }

    private _resetFormVisibility(form: HTMLElement): void {
        form.style.visibility = "";
    }

    //#endregion Methods
}
